import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { SensorPropertiesService } from '@services/sensor-properties.service';

export interface LevelEmbalseChangeEvent {
  deviceSensorId: string;
  isDirty: boolean;
  valuesToUpsert: Record<string, any> | null;
}

@Component({
  selector: 'app-level-embalse',
  templateUrl: './level-embalse.component.html',
  styleUrls: ['./level-embalse.component.scss'],
  standalone: false,
})
export class LevelEmbalseComponent implements OnChanges, OnDestroy {

  @Input() deviceSensorId!: string;
  @Input() commitToken: number = 0;

  @Output() changeState = new EventEmitter<LevelEmbalseChangeEvent>();

  form: UntypedFormGroup;

  loaded = false;
  loading = false;
  deleting = false;
  maxCapacity: number | null = null;

  private initialSnapshotJson = '';
  private sub: Subscription;

  constructor(
    private fb: UntypedFormBuilder,
    private sensorPropsService: SensorPropertiesService,
  ) {
    this.form = this.fb.group({
      baseSmall: [null, [Validators.min(0)]],
      baseLarge: [null, [Validators.min(0)]],
      height:    [null, [Validators.min(0.01)]],
    });

    this.sub = this.form.valueChanges.subscribe(() => {
      this.calculateMaxCapacity();
      this.emitState();
    });
  }

  onOpened(): void {
    if (this.loaded || this.loading) return;
    if (!this.deviceSensorId) return;

    this.loading = true;

    this.sensorPropsService
      .getProperties(this.deviceSensorId)
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: (res) => {
          const v = res?.values ?? {};
          const baseSmall   = v['baseSmall']   ? parseFloat(v['baseSmall'])   : null;
          const baseLarge   = v['baseLarge']   ? parseFloat(v['baseLarge'])   : null;
          const height      = v['height']      ? parseFloat(v['height'])      : null;
          const maxCapacity = v['maxCapacity'] ? parseFloat(v['maxCapacity']) : null;

          this.form.patchValue({ baseSmall, baseLarge, height }, { emitEvent: false });
          this.maxCapacity = maxCapacity;

          this.loaded = true;
          this.captureSnapshot();
          this.emitState();
        },
        error: () => {
          this.loaded = true;
          this.captureSnapshot();
          this.emitState();
        },
      });
  }

  deleteData(): void {
    if (!this.deviceSensorId || this.deleting) return;

    this.deleting = true;
    this.sensorPropsService
      .deleteProperties(this.deviceSensorId)
      .pipe(finalize(() => (this.deleting = false)))
      .subscribe({
        next: () => {
          this.form.reset({ baseSmall: null, baseLarge: null, height: null }, { emitEvent: false });
          this.maxCapacity = null;
          this.captureSnapshot();
          this.emitState();
        },
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['commitToken'] && !changes['commitToken'].firstChange) {
      this.captureSnapshot();
      this.emitState();
    }
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  private captureSnapshot(): void {
    this.initialSnapshotJson = JSON.stringify({ ...this.form.getRawValue(), maxCapacity: this.maxCapacity });
    this.form.markAsPristine();
  }

  private isReallyDirty(): boolean {
    const current = JSON.stringify({ ...this.form.getRawValue(), maxCapacity: this.maxCapacity });
    return current !== this.initialSnapshotJson;
  }

  private emitState(): void {
    if (!this.deviceSensorId) return;
    if (this.maxCapacity == null && this.canCalculate) {
      this.calculateMaxCapacity();
    }

    const isDirty = this.isReallyDirty();
    const valuesToUpsert = isDirty && this.maxCapacity != null ? this.buildValues() : null;

    this.changeState.emit({ deviceSensorId: this.deviceSensorId, isDirty, valuesToUpsert });
  }

  private buildValues(): Record<string, any> {
    const { baseSmall, baseLarge, height } = this.form.value;
    return {
      baseSmall:   String(baseSmall),
      baseLarge:   String(baseLarge),
      height:      String(height),
      maxCapacity: String(this.maxCapacity),
    };
  }

  get canCalculate(): boolean {
    const { baseSmall, baseLarge, height } = this.form.value;
    return baseSmall > 0 && baseLarge > 0 && height > 0;
  }

  private calculateMaxCapacity(): void {
    const { baseSmall, baseLarge, height } = this.form.value;
    if (baseSmall == null || baseLarge == null || height == null || height <= 0) {
      this.maxCapacity = null;
      return;
    }

    const total = (height / 3) * (baseSmall + baseLarge + Math.sqrt(baseSmall * baseLarge));
    this.maxCapacity = Math.round(total * 100) / 100;
  }
}
